import { useState, useEffect, useCallback, useRef } from "react";
import { axiosInstance } from "../App";
import secureStorage from "../utils/secureStorage";
import { useNavigate } from "react-router-dom";
import { Baby, Palette, FileText, Receipt, CreditCard, Calendar, Loader2, Clock, CheckCircle, Eye, ExternalLink, RefreshCw, ArrowLeft, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import useFeaturePermissions from "../hooks/useFeaturePermissions";
import { PageLayout } from "../components/layout/Sidebar";
import PullToRefresh from "../components/common/PullToRefresh";
import ReactCalendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import RegistrationRequiredModal from "../components/auth/RegistrationRequiredModal";
import ParentNotifications from "../components/notifications/ParentNotifications";
import MiniRadar from "../components/development/MiniRadar";
import ParentTermReport from "../components/development/ParentTermReport";
import TermReleaseBanner from "../components/development/TermReleaseBanner";
import PushNotificationPrompt from "../components/notifications/PushNotificationPrompt";
import EmailLinkModal from "../components/auth/EmailLinkModal";
import { FloatingElements, AnimatedStatCard, AnimatedHeader, QuickActionButton, GreetingBanner, ChildCard, AnimatedCard, ParentIcon } from "../components/ui/AnimatedElements";
import { BirthdayAnimation, BirthdayNotificationCard, useBirthdayChildren } from "../components/notifications/BirthdayNotification";
import { getLocalDateString } from "../lib/utils";
import { FarewellCountdown } from "../components/survey/FarewellCountdown";
import { AccessEndingBanner } from "../components/survey/AccessEndingBanner";
import { SurveyPendingPrompt } from "../components/survey/SurveyPendingPrompt";
import { AlbumDownloadCard } from "../components/survey/AlbumDownloadCard";

export default function ParentDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const { hasAccess } = useFeaturePermissions(user?.role);
  const { birthdayChildren, showAnimation, closeAnimation } = useBirthdayChildren(user);
  const [stats, setStats] = useState({});
  const [devOverview, setDevOverview] = useState([]);
  const [termSummary, setTermSummary] = useState([]);
  const [children, setChildren] = useState([]);
  const [farewell, setFarewell] = useState({});
  useEffect(() => {
    axiosInstance.get('/survey/kids2027/my-farewell').then((r) => setFarewell(r.data || {})).catch(() => {});
  }, []);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [generatingBillplz, setGeneratingBillplz] = useState(null);
  const [showRegistrationPopup, setShowRegistrationPopup] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [showEmailLinkModal, setShowEmailLinkModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const abandonedCheckRef = useRef(null);
  
  // Check if this is an impersonated session
  const isImpersonating = !!secureStorage.getItem("original_manager_token");
  const originalManagerUser = isImpersonating ? JSON.parse(secureStorage.getItem("original_manager_user") || "{}") : null;

  const handleExitImpersonation = () => {
    const originalToken = secureStorage.getItem("original_manager_token");
    const originalUser = secureStorage.getItem("original_manager_user");
    
    if (originalToken && originalUser) {
      secureStorage.setItem("token", originalToken);
      secureStorage.setItem("user", originalUser);
      secureStorage.removeItem("original_manager_token");
      secureStorage.removeItem("original_manager_user");
      
      toast.success("Kembali ke Dashboard Pengurus");
      window.location.href = "/";
    }
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/stats');
      setStats(response.data);
    } catch (error) {
      toast.error("Ralat mendapatkan statistik");
    }
  }, []);

  const fetchChildren = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/children');
      setChildren(response.data);
    } catch (error) {
      toast.error("Ralat mendapatkan data kanak-kanak");
    }
  }, []);

  // Handle photo upload for children
  const handlePhotoUpload = async (childId, file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Sila pilih fail gambar sahaja");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Saiz gambar terlalu besar (maksimum 5MB)");
      return;
    }
    
    setUploadingPhoto(childId);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axiosInstance.post(`/children/${childId}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update child in state with new photo URL
      setChildren(prev => prev.map(child => 
        child.id === childId 
          ? { ...child, photo_url: response.data.photo_url }
          : child
      ));
      
      toast.success("✅ Gambar profil berjaya dikemas kini!");
    } catch (error) {
      console.error("Photo upload error:", error);
      toast.error(error.response?.data?.detail || "Gagal memuat naik gambar");
    } finally {
      setUploadingPhoto(null);
    }
  };

  const fetchInvoices = useCallback(async () => {
    try {
      // Use auto-sync for real-time invoice data
      const response = await axiosInstance.get('/billing/invoices');
      setInvoices(response.data.invoices || response.data || []);
    } catch (error) {
    }
  }, []);

  const checkRegistrationStatus = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/parent/registration-status');
      setRegistrationStatus(response.data);
      if (response.data.require_registration_popup) {
        setShowRegistrationPopup(true);
      }
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    const loadDashboardData = () => {
      axiosInstance.get('/stats').then((r) => setStats(r.data)).catch(() => {});
      axiosInstance.get('/children').then((r) => setChildren(r.data)).catch(() => {});
      axiosInstance.get('/development/overview').then((r) => setDevOverview(r.data || [])).catch(() => {});
      axiosInstance.get('/development/parent-term-summary').then((r) => setTermSummary(r.data || [])).catch(() => {});
      fetchInvoices();
      axiosInstance.get('/billing/auto-sync-invoiless').then(() => setTimeout(fetchInvoices, 15000)).catch(() => {});
      axiosInstance.get('/parent/registration-status').then((r) => {
        const regData = r.data;
        setRegistrationStatus(regData);
        if (regData.require_registration_popup) {
          setShowRegistrationPopup(true);
        }
        if (regData.require_email_link && !regData.require_registration_popup) {
          const skippedTime = localStorage.getItem("email_link_skipped");
          const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
          if (!skippedTime || (Date.now() - parseInt(skippedTime)) > sevenDaysMs) {
            setTimeout(() => setShowEmailLinkModal(true), 1500);
          }
        }
      }).catch(() => {});
    };
    loadDashboardData();
    
    // Auto-refresh invoices every 30 seconds for real-time updates
    const invoicePollingInterval = setInterval(() => {
      fetchInvoices();
    }, 30000);
    
    return () => clearInterval(invoicePollingInterval);
  }, [fetchInvoices]);

  const handlePullRefresh = async () => {
    await Promise.all([fetchStats(), fetchChildren(), fetchInvoices(), checkRegistrationStatus()]);
    toast.success("Data dikemaskini! 🎉");
  };

  const generateBillplzLink = async (invoiceId) => {
    setGeneratingBillplz(invoiceId);
    try {
      // Track that payment was initiated
      await axiosInstance.post(`/billing/track-payment-start/${invoiceId}`);
      
      const response = await axiosInstance.post(`/billing/generate-billplz/${invoiceId}`);
      if (response.data.billplz_url) {
        window.open(response.data.billplz_url, '_blank');
        toast.success("Redirecting ke Billplz...");
        
        // Set timer to check for abandoned payment after 2 minutes
        if (abandonedCheckRef.current) {
          clearTimeout(abandonedCheckRef.current);
        }
        abandonedCheckRef.current = setTimeout(async () => {
          try {
            const checkResponse = await axiosInstance.get('/billing/check-abandoned-payments');
            const abandoned = checkResponse.data.abandoned_payments || [];
            const thisInvoice = abandoned.find(inv => inv.id === invoiceId);
            
            if (thisInvoice) {
              // Send reminder notification
              await axiosInstance.post(`/billing/send-abandoned-reminder/${invoiceId}`);
              toast.info("⏰ Pembayaran belum selesai. Peringatan telah dihantar.", {
                duration: 5000,
                action: {
                  label: "Bayar Sekarang",
                  onClick: () => window.open(thisInvoice.billplz_url, '_blank')
                }
              });
            }
          } catch (e) {
          }
        }, 2 * 60 * 1000); // 2 minutes
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat menjana link pembayaran");
    } finally {
      setGeneratingBillplz(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid': return <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-xs font-black bg-green-100 text-green-600">✅ Sudah Bayar</span>;
      case 'pending': return <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-xs font-black bg-yellow-100 text-yellow-600">⏳ Menunggu</span>;
      case 'overdue': return <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-xs font-black bg-red-100 text-red-600">⚠️ Tertunggak</span>;
      default: return <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-xs font-black bg-gray-100 text-gray-600">Belum Bayar</span>;
    }
  };

  return (
    <PageLayout user={user} onLogout={onLogout} hasAccess={hasAccess}>
      <FloatingElements variant="default" />
      
      {/* Impersonation Banner - Show when branch manager is viewing as parent */}
      {isImpersonating && (
        <>
          {/* Mobile Banner - Positioned directly below mobile header with NO gap */}
          <div 
            className="lg:hidden fixed left-0 right-0 z-50 bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 52px)' }}
          >
            <div className="py-2.5 px-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Eye className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold truncate">Paparan: {user?.name}</p>
                  </div>
                </div>
                <Button
                  onClick={handleExitImpersonation}
                  size="sm"
                  className="bg-white text-purple-600 hover:bg-purple-50 font-bold rounded-xl text-xs px-3 py-1.5 h-auto flex-shrink-0 shadow-md"
                  data-testid="exit-impersonation-btn-mobile"
                >
                  <ArrowLeft className="w-3 h-3 mr-1" />
                  Kembali
                </Button>
              </div>
            </div>
          </div>
          
          {/* Desktop Banner */}
          <div className="hidden lg:block fixed top-0 left-64 right-0 z-40 bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg">
            <div className="py-3 px-4">
              <div className="max-w-6xl mx-auto flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Eye className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-bold text-sm">Mod Paparan Sebagai Ibu Bapa</p>
                    <p className="text-xs text-white/80">Anda sedang melihat sebagai: {user?.name}</p>
                  </div>
                </div>
                <Button
                  onClick={handleExitImpersonation}
                  className="bg-white text-purple-600 hover:bg-purple-50 font-bold rounded-xl shadow-lg"
                  data-testid="exit-impersonation-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Kembali ke Dashboard
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* Birthday Animation for Parents */}
      {showAnimation && birthdayChildren.length > 0 && (
        <BirthdayAnimation children={birthdayChildren} onClose={closeAnimation} />
      )}
      
      <RegistrationRequiredModal 
        isOpen={showRegistrationPopup}
        onClose={() => setShowRegistrationPopup(false)}
        pendingRegistrations={registrationStatus?.pending_registrations || []}
      />
      
      {/* Email Link Modal - untuk ibu bapa yang belum masukkan emel */}
      <EmailLinkModal
        isOpen={showEmailLinkModal}
        onClose={() => setShowEmailLinkModal(false)}
        user={user}
        onSuccess={(email) => {
          // Update local user data
          const updatedUser = { ...user, email };
          secureStorage.setItem("user", JSON.stringify(updatedUser));
        }}
      />
      
      <PullToRefresh onRefresh={handlePullRefresh}>
      <div className={`max-w-6xl mx-auto relative z-10 ${isImpersonating ? 'mt-12 lg:mt-16' : ''}`}>
        
        <GreetingBanner userName={user.name} role={user.role} userPhoto={user.photo_url} />
        
        {/* Banner ceria: Laporan Penggal baru dirilis (7 hari dari tarikh rilis) */}
        <TermReleaseBanner
          items={termSummary}
          onView={() => {
            const el = document.getElementById("laporan-penggal-section");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        />

        {/* Birthday Notification Card */}
        {birthdayChildren.length > 0 && (
          <BirthdayNotificationCard children={birthdayChildren} userRole={user.role} />
        )}
        
        <div className="flex items-center justify-between mb-6">
          <AnimatedHeader 
            customIcon={
              <div className="flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 sm:w-7 sm:h-7 text-white">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
            }
            title="Dashboard Ibu Bapa"
            subtitle="🎈 Pantau perkembangan anak anda!"
            delay={100}
          />
          <div className="flex items-center gap-2">
            <PushNotificationPrompt user={user} />
            <ParentNotifications user={user} />
          </div>
        </div>

        {/* Warning Banner */}
        {registrationStatus && !registrationStatus.has_registered_children && (
          <div className="relative overflow-hidden bg-gradient-to-r from-orange-400 to-yellow-400 rounded-3xl p-5 mb-6 shadow-xl text-white">
            <div className="absolute top-2 right-4 text-4xl opacity-50">📝</div>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/30 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="font-black text-xl">Pendaftaran Anak Diperlukan!</p>
                <p className="text-white/90 font-bold mt-1">
                  Sila daftarkan anak anda untuk mengakses semua ciri sistem.
                </p>
                <Button 
                  className="mt-3 bg-white text-orange-500 hover:bg-orange-50 font-black rounded-2xl shadow-lg"
                  onClick={() => navigate('/child-registration')}
                >
                  Daftar Anak Sekarang 🚀
                </Button>
              </div>
            </div>
          </div>
        )}

        {farewell.show_access_banner && <AccessEndingBanner label={farewell.access_end_label} />}
        <SurveyPendingPrompt userId={user?.id} />
        {farewell.can_export_album && <AlbumDownloadCard />}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4 lg:gap-6 mb-8">
          <AnimatedStatCard emoji="👶" label="Anak Saya" value={stats.my_children || 0} color="red" delay={200} />
          <AnimatedStatCard emoji="🎨" label="Aktiviti Hari Ini" value={stats.today_activities || 0} color="blue" delay={300} />
        </div>

        {/* Children List */}
        {children.length > 0 && (
          <AnimatedCard className="mb-8 cursor-pointer hover:scale-[1.01] transition-transform" delay={400} color="orange" onClick={() => navigate('/children')}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-400 to-yellow-400 flex items-center justify-center shadow-lg">
                <span className="text-2xl">👶</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-black text-gray-800 flex-1">Anak-anak Saya</h2>
              <span className="bg-orange-100 text-orange-600 px-4 py-1.5 rounded-full font-black text-sm whitespace-nowrap">
                {children.length}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {children.map((child, index) => (
                <ChildCard 
                  key={child.id}
                  child={child}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/nutritional-status/${child.id}`);
                  }}
                  delay={500 + (index * 100)}
                  onPhotoUpload={handlePhotoUpload}
                  uploadingPhoto={uploadingPhoto}
                  extra={farewell.child_ids?.includes(child.id) ? <FarewellCountdown sessionEnd={farewell.session_end} /> : null}
                />
              ))}
            </div>
            <p className="text-center text-sm text-orange-500 font-bold mt-4">
              Tekan untuk lihat semua anak →
            </p>
          </AnimatedCard>
        )}

        {/* Radar Perkembangan Anak */}
        {devOverview.length > 0 && (
          <AnimatedCard className="mb-8" delay={450} color="blue">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg">
                <span className="text-2xl">📊</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-black text-gray-800 flex-1">Radar Perkembangan Anak</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {devOverview.map((c) => (
                <div
                  key={c.child_id}
                  data-testid={`dev-radar-${c.child_id}`}
                  onClick={() => navigate('/development')}
                  className="bg-white/90 rounded-2xl p-4 shadow-sm border border-blue-100 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  <MiniRadar domains={c.domains} size={160} />
                  <p className="text-sm font-black text-gray-800 text-center mt-1 truncate">{c.child_name}</p>
                  <p className="text-xs font-bold text-blue-600 text-center mt-0.5">
                    {c.achieved}/{c.total} milestone · {c.percentage}%
                  </p>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-blue-500 font-bold mt-4">
              Tekan untuk lihat rekod perkembangan penuh →
            </p>
          </AnimatedCard>
        )}

        {/* Laporan Penggal: komen guru terkini + perbandingan kemajuan */}
        {termSummary.length > 0 && (
          <div id="laporan-penggal-section" className="scroll-mt-24">
          <AnimatedCard className="mb-8" delay={500} color="purple">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-400 to-fuchsia-500 flex items-center justify-center shadow-lg">
                <MessageSquareQuote className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl sm:text-2xl font-black text-gray-800">Laporan Penggal</h2>
                <p className="text-sm text-gray-500">Komen guru terkini &amp; perbandingan kemajuan anak</p>
              </div>
            </div>
            <ParentTermReport items={termSummary} onOpen={() => navigate('/development')} />
          </AnimatedCard>
          </div>
        )}

        {/* Quick Actions */}
        <AnimatedCard className="mb-8" delay={600} color="green">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-400 to-cyan-400 flex items-center justify-center shadow-lg">
              <span className="text-2xl">⚡</span>
            </div>
            <h2 className="text-2xl font-black text-gray-800">Akses Pantas</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <QuickActionButton emoji="📝" label="Daftar Anak" sublabel="Pendaftaran baru" color="red" onClick={() => navigate('/child-registration')} delay={700} />
            <QuickActionButton emoji="🎨" label="Aktiviti" sublabel="Lihat aktiviti" color="orange" onClick={() => navigate('/activities')} delay={800} />
            <QuickActionButton emoji="✅" label="Kehadiran" sublabel="Rekod kehadiran" color="teal" onClick={() => navigate('/attendance')} delay={850} />
            <QuickActionButton emoji="📊" label="Perkembangan" sublabel="Pantau progress" color="blue" onClick={() => navigate('/development')} delay={900} />
            <QuickActionButton emoji="📖" label="Panduan" sublabel="Cara guna apps" color="purple" onClick={() => navigate('/panduan-ibu-bapa')} delay={1000} />
          </div>
        </AnimatedCard>

        {/* Invoices Section - Enhanced Design */}
        <AnimatedCard className="mb-8" delay={800} color="purple">
          {/* Header with gradient background */}
          <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
                <Receipt className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-black text-gray-800">Invois & Pembayaran</h2>
                <p className="text-xs text-gray-500 font-medium">
                  {invoices.filter(i => i.status?.toLowerCase() !== 'paid').length > 0 
                    ? `${invoices.filter(i => i.status?.toLowerCase() !== 'paid').length} invois belum dibayar`
                    : 'Semua invois sudah dijelaskan'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowCalendar(!showCalendar)}
                className="border-purple-300 text-purple-600 hover:bg-purple-50 rounded-xl font-bold"
              >
                <Calendar className="w-4 h-4 mr-1" />
                {showCalendar ? 'Tutup' : 'Kalendar'}
              </Button>
            </div>
          </div>

          {/* Quick Stats */}
          {invoices.length > 0 && (
            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-lg font-black text-gray-800">
                      RM {invoices.filter(i => i.status?.toLowerCase() !== 'paid').reduce((sum, i) => sum + (i.amount || 0), 0).toFixed(0)}
                    </p>
                    <p className="text-xs text-gray-500 font-medium">Tertunggak</p>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-200">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-lg font-black text-gray-800">
                      {invoices.filter(i => i.status?.toLowerCase() === 'paid').length}
                    </p>
                    <p className="text-xs text-gray-500 font-medium">Dibayar</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showCalendar && (
            <div className="mb-5 p-4 bg-purple-50 rounded-2xl border-2 border-purple-200">
              <ReactCalendar 
                onChange={setSelectedDate} 
                value={selectedDate} 
                className="rounded-xl border-0 shadow-lg"
                tileContent={({ date }) => {
                  // Check if any invoice was created on this date
                  const dateStr = getLocalDateString(date);
                  const hasInvoice = invoices.some(inv => {
                    const invDate = inv.created_at || inv.due_date || '';
                    return invDate.startsWith(dateStr);
                  });
                  return hasInvoice ? (
                    <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mx-auto mt-0.5"></div>
                  ) : null;
                }}
              />
              {/* Show invoices for selected date */}
              <div className="mt-4 pt-4 border-t border-purple-200">
                <p className="text-sm font-bold text-gray-700 mb-2">
                  {selectedDate.toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
                {(() => {
                  const dateStr = getLocalDateString(selectedDate);
                  const dateInvoices = invoices.filter(inv => {
                    const invDate = inv.created_at || inv.due_date || '';
                    return invDate.startsWith(dateStr);
                  });
                  return dateInvoices.length === 0 ? (
                    <p className="text-sm text-gray-400">Tiada invois pada tarikh ini</p>
                  ) : (
                    <div className="space-y-2">
                      {dateInvoices.map(inv => {
                        const isPaid = inv.status?.toLowerCase() === 'paid';
                        return (
                          <button 
                            key={inv.id} 
                            onClick={() => { setSelectedInvoice(inv); setShowInvoiceDetail(true); }}
                            className={`w-full p-3 rounded-xl text-left transition-colors ${
                              isPaid ? 'bg-emerald-50 hover:bg-emerald-100' : 'bg-amber-50 hover:bg-amber-100'
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700 truncate">{inv.description || inv.invoice_number}</span>
                              <span className={`font-bold ${isPaid ? 'text-emerald-600' : 'text-amber-600'}`}>
                                RM {(inv.amount || 0).toFixed(2)}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {invoices.length > 0 ? (
            <div className="space-y-3">
              {invoices.slice(0, 5).map((invoice, index) => {
                const isPaid = invoice.status?.toLowerCase() === 'paid';
                const isPending = invoice.status?.toLowerCase() === 'pending';
                return (
                  <div 
                    key={invoice.id}
                    className={`p-4 rounded-2xl border-l-4 transition-all duration-300 cursor-pointer group hover:shadow-lg active:scale-[0.98] ${
                      isPaid 
                        ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-l-emerald-500' 
                        : isPending 
                        ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-l-amber-500'
                        : 'bg-gradient-to-r from-rose-50 to-red-50 border-l-rose-500'
                    }`}
                    onClick={() => { setSelectedInvoice(invoice); setShowInvoiceDetail(true); }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-800 truncate">{invoice.description || `Invois #${invoice.id?.slice(0,8)}`}</p>
                        {/* Show Invoiless link if available */}
                        {invoice.invoiless_url ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(invoice.invoiless_url, '_blank');
                            }}
                            className="text-xs text-purple-600 font-medium flex items-center gap-1 hover:text-purple-800 mt-1"
                          >
                            <Eye className="w-3 h-3" />
                            Lihat di Invoiless
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        ) : (
                          <p className="text-xs text-gray-400 mt-1">
                            {invoice.created_at?.split('T')[0] || '-'}
                          </p>
                        )}
                      </div>
                      <div className="text-right ml-3">
                        <p className="text-xl font-black text-gray-800">RM {(invoice.amount || 0).toFixed(2)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                          isPaid 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : isPending 
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-rose-100 text-rose-700'
                        }`}>
                          {isPaid ? 'Dibayar' : isPending ? 'Menunggu' : 'Belum Bayar'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {invoices.length > 5 && (
                <Button 
                  variant="outline" 
                  className="w-full rounded-2xl border-2 border-purple-300 text-purple-600 font-bold hover:bg-purple-50 active:scale-[0.98] transition-transform" 
                  onClick={() => navigate('/billing')}
                >
                  Lihat Semua Invois ({invoices.length})
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mx-auto mb-3 shadow-lg">
                <Receipt className="w-10 h-10 text-purple-400" />
              </div>
              <h3 className="font-bold text-gray-700 mb-1">Tiada Invois</h3>
              <p className="text-sm text-gray-400">Semua invois sudah dijelaskan!</p>
            </div>
          )}
        </AnimatedCard>

        {/* Invoice Detail Modal - Enhanced Design */}
        <Dialog open={showInvoiceDetail} onOpenChange={setShowInvoiceDetail}>
          <DialogContent className="max-w-sm mx-auto p-0 overflow-hidden bg-white/95 backdrop-blur-xl border-2 border-purple-200 rounded-[28px]">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-100 via-pink-100 to-purple-100 p-5 text-center">
              <Receipt className="w-10 h-10 text-purple-500 mx-auto mb-2" />
              <DialogTitle className="text-lg font-black text-gray-800">Butiran Invois</DialogTitle>
            </div>
            
            {selectedInvoice && (
              <div className="p-5 space-y-4">
                {/* Amount Display */}
                <div className="text-center py-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl">
                  <p className="text-sm text-gray-500 mb-1">Jumlah</p>
                  <p className="text-4xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    RM {((selectedInvoice.amount || 0) + (selectedInvoice.status?.toLowerCase() !== 'paid' ? 1 : 0)).toFixed(2)}
                  </p>
                  {selectedInvoice.status?.toLowerCase() !== 'paid' && (
                    <p className="text-xs text-gray-400 mt-1">
                      RM {(selectedInvoice.amount || 0).toFixed(2)} + RM 1.00 (Yuran Transaksi)
                    </p>
                  )}
                </div>
                
                {/* Child Name & Month */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 rounded-xl text-center">
                    <p className="text-xs text-gray-400 mb-1">Nama Anak</p>
                    <p className="font-bold text-gray-700 text-sm">
                      {(() => {
                        const invoiceNum = selectedInvoice.invoice_number || '';
                        const parentName = selectedInvoice.parent_name || '';
                        const match = parentName.match(/\(([^)]+)\)/);
                        if (match) return match[1];
                        const parts = invoiceNum.split(' ');
                        if (parts.length > 1) return parts.slice(1).join(' ');
                        return selectedInvoice.child_name || 'Nama Anak';
                      })()}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl text-center">
                    <p className="text-xs text-gray-400 mb-1">Bulan</p>
                    <p className="font-bold text-gray-700 text-sm">
                      {(() => {
                        const invoiceNum = selectedInvoice.invoice_number || '';
                        const createdAt = selectedInvoice.created_at || '';
                        const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                        const monthsMalay = ['Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun', 'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'];
                        for (let i = 0; i < months.length; i++) {
                          if (invoiceNum.toLowerCase().includes(months[i].toLowerCase())) {
                            return monthsMalay[i];
                          }
                        }
                        if (createdAt) {
                          const date = new Date(createdAt);
                          return monthsMalay[date.getMonth()];
                        }
                        return selectedInvoice.month_year || 'Bulan';
                      })()}
                    </p>
                  </div>
                </div>
                
                {/* Status Badge */}
                <div className="flex justify-center">
                  <span className={`px-5 py-2 rounded-full text-sm font-bold flex items-center gap-2 ${
                    selectedInvoice.status?.toLowerCase() === 'paid' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : selectedInvoice.status?.toLowerCase() === 'pending'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-rose-100 text-rose-700'
                  }`}>
                    {selectedInvoice.status?.toLowerCase() === 'paid' ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <Clock className="w-4 h-4" />
                    )}
                    {selectedInvoice.status?.toLowerCase() === 'paid' ? 'Dibayar' : selectedInvoice.status?.toLowerCase() === 'pending' ? 'Menunggu' : 'Belum Bayar'}
                  </span>
                </div>
                
                {/* Payment Button */}
                {selectedInvoice.status?.toLowerCase() !== 'paid' && (
                  <Button 
                    className="w-full h-12 font-bold rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg shadow-purple-500/30 active:scale-[0.98] transition-transform"
                    onClick={() => generateBillplzLink(selectedInvoice.id)}
                    disabled={generatingBillplz === selectedInvoice.id}
                  >
                    {generatingBillplz === selectedInvoice.id ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        Menjana link...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Bayar Sekarang
                      </>
                    )}
                  </Button>
                )}
                
                {/* Invoiless Button */}
                {selectedInvoice.invoiless_url && (
                  <Button
                    variant="outline"
                    className="w-full h-11 font-bold rounded-2xl border-2 border-purple-200 text-purple-600 hover:bg-purple-50 active:scale-[0.98] transition-transform"
                    onClick={() => window.open(selectedInvoice.invoiless_url, '_blank')}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Lihat di Invoiless
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

      </div>
      </PullToRefresh>
    </PageLayout>
  );
}
